<script>
    import { onDestroy, onMount } from "svelte";
    import statisticService from "./service.js";
    import SeverityTotals from "../vulnerability/_components/severityTotals/v2-severity-totals.svelte";
    import FindingsByAge from "./_components/findings-by-age.svelte";
    import FindingsBySeverity from "../finding/_components/findings-by-severity.svelte";
    import DailyMaxCount from "./_components/daily-max-count.svelte";
    import StatusMaxCount from "./_components/status-max-count.svelte";
    import { token, user } from "../user/user.state.svelte.js";
    import { notificate } from "../../services/notification.js";
    import userService from "../user/service.svelte.js";
    import slaService from "../sla/service.js";
    import { searchAssetsCountByKind } from "../asset/assets.state.svelte.js";
    import { format, locale } from "svelte-i18n";
    import dayjs from "dayjs";

    // Importamos PowerTable
    import { PowerTable } from "@muonw/powertable";

    let refreshStatistics = $state(false);
    let is_tab_active = !document.hidden;
    let prevInterval;
    let severities_days = $state();
    let status_days = $state();
    let findingSeverityAge = {};
    let findingCount = {};
    let dailyMaxCountPromises = $state();
    let dailyStatusCountPromises = $state();
    let assetTotalsPromise;
    let severityTotalsPromise = $state();
    let findingCountPromise = $state();
    let findingSeverityAgePromise = $state();
    let owasp10sPromise;
    let cvesPromise;
    let applicationsPromise;
    let op_systemsPromise;
    let findingsCriticalPromise;
    let findingsHighPromise;
    let findingsMediumPromise;
    let findingsLowPromise;
    let findingsOpenPromise;
    let findingsAcceptedPromise;
    let findingsFixedPromise;
    let findingsFalsePositivePromise;
    let show_welcome_message = $state(user.data.show_welcome_message);
    let showed_welcome_once = $state(user.data.showed_welcome_once);

    let dailyMaxCountData = {
        findingsCriticalData: [],
        findingsHighData: [],
        findingsMediumData: [],
        findingsLowData: [],
    };

    let dailyStatusCountData = {
        findingsOpenData: [],
        findingsAcceptedData: [],
        findingsFixedData: [],
        findingsFalsePositiveData: [],
    };

    let showHelp = $state({
        vuls_by_age: { persistent: false, mouseover: false },
        vuls_by_severity: { persistent: false, mouseover: false },
        daily_max_count: { persistent: false, mouseover: false },
        status_max_count: { persistent: false, mouseover: false },
        top_cve: { persistent: false, mouseover: false },
        owasp_top_10: { persistent: false, mouseover: false },
        top_app: { persistent: false, mouseover: false },
        top_os: { persistent: false, mouseover: false },
        last_vuls: { persistent: false, mouseover: false },
        patching_roadmap: { persistent: false, mouseover: false },
    });

    // interval, updated_at
    let dataUpdate = $state({
        interval: null, // minutes
        updated_at: new Date(),
    });

    // --- Variables y Funciones para Patching Roadmap con PowerTable ---
    let roadmapData = $state([]);
    let roadmapTableElement = $state();
    let is_requesting_roadmap = $state(false);
    let minPriority = $state(4.0);

    const sortNumbers = (v1, v2) => {
        return parseFloat(v1) < parseFloat(v2)
            ? -1
            : parseFloat(v1) > parseFloat(v2)
              ? 1
              : 0;
    };

    const parsePatchingRows = (pageContent) => {
        pageContent.forEach((row) => {
            if (row["max_priority"]) {
                row["max_priority"] =
                    `<div class="cvss-badge ${getSeverityBadge(row.max_priority).class || "none"}" style="--mwidth: 70px">${parseFloat(row.max_priority).toPrecision(2)}</div>`;
            }
        });
        return pageContent;
    };

    let ptOptionsRoadmap = $state({
        uniquePrefix: "roadmapTable",
        rowsPerPageOptions: [5, 10, 20, 50],
        footerText: true,
        footerFilters: false,
        footerLoadingBar: false,
        footerHeaders: false,
        segments: {
            topBar: ["search"], // Barra de búsqueda habilitada
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {
            next: $format("list.interface.next"),
            of: $format("list.interface.of"),
            previous: $format("list.interface.previous"),
            rows: $format("list.interface.rows"),
            search: $format("list.interface.search"),
            filterBy: $format("list.interface.filterBy"),
        },
        isDataRemote: false,
        userFunctions: {
            customParse: parsePatchingRows,
        },
    });

    let instructsRoadmap = [
        {
            key: "cpe",
            title: "CPE",
            filterable: false,
            sortable: true,
            parseAs: "unsafe-html",
        },
        {
            key: "max_priority",
            title: $format("list.columns.max_priority"),
            filterable: false,
            sortable: true,
            parseAs: "unsafe-html",
            userFunctions: { customSort: sortNumbers },
        },
        {
            key: "vulnerability_count",
            title: $format("list.columns.vulnerabilities"),
            filterable: false,
            sortable: true,
            parseAs: "unsafe-html",
        },
        {
            key: "finding_count",
            title: $format("list.columns.findings"),
            filterable: false,
            sortable: true,
            parseAs: "unsafe-html",
        },
        {
            key: "impacted_assets",
            title: $format("list.columns.assets"),
            filterable: false,
            sortable: true,
        },
        {
            key: "finding_weight",
            title: $format("list.columns.risk_score"),
            filterable: false,
            sortable: true,
            parseAs: "unsafe-html",
        },
    ];

    const getSeverityBadge = (score) => {
        if (score >= 9.0) return { class: "cvss-critical", label: "Crítica" };
        if (score >= 7.0) return { class: "cvss-high", label: "Alta" };
        if (score >= 4.0) return { class: "cvss-medium", label: "Media" };
        return { class: "cvss-low", label: "Baja" };
    };

    const loadPatchingRoadmap = () => {
        is_requesting_roadmap = true;
        let config = {
            filter: { min_priority: minPriority ? minPriority : 0 },
        };
        statisticService
            .getPatchingRoadmap(config)
            .then((res) => {
                const items = res.items || res; // Soporte por si la API devuelve directamente el array o dentro de .items
                if (items && Array.isArray(items)) {
                    // padNum nos asegura que PowerTable ordene bien los strings HTML que contienen números
                    const padNum = (num) => String(num).padStart(10, "0");
                    roadmapData = items.map((item) => {
                        const badge = getSeverityBadge(item.max_priority);
                        return {
                            id: item.cpe,
                            cpe: `<code class="text-dark">${item.cpe}</code>`,
                            max_priority: item.max_priority,
                            vulnerability_count: `<span style="display:none">${padNum(item.vulnerability_count)}</span><a href="/vulnerability?property_name=${encodeURIComponent(item.cpe)}" class="fw-bold">${item.vulnerability_count}</a>`,
                            finding_count: `<span style="display:none">${padNum(item.finding_count)}</span><a href="/finding?property_kind=affected_product&property_name=${encodeURIComponent(item.cpe)}" class="fw-bold">${item.finding_count}</a>`,
                            impacted_assets: item.impacted_assets,
                            finding_weight: `<span style="display:none">${padNum(item.finding_weight)}</span><span class="fw-bold">${item.finding_weight}</span>`,
                        };
                    });
                } else {
                    roadmapData = [];
                }
            })
            .catch((e) => {
                roadmapData = [];
            })
            .finally(() => {
                is_requesting_roadmap = false;
            });
    };

    /**
     * Whenever the data gets updated
     * update updated_at timestamp
     */
    let handleUpdateData = async (
        severities_time,
        status_time,
        refreshNumbers,
    ) => {
        if (refreshNumbers) refreshStatistics = true;

        if (severities_time) severities_time++;
        if (status_time) status_time++;

        if (!severities_time) severities_time = "31";
        if (!status_time) status_time = "31";

        if (severities_days) severities_days = severities_time - 1;
        if (status_days) status_days = status_time - 1;

        // Cargar Patching Roadmap
        loadPatchingRoadmap();

        severityTotalsPromise = statisticService
            .get("vulnerability-count")
            .then((vulCount) => {
                // sort object keys by severity
                return {
                    critical: vulCount.critical,
                    high: vulCount.high,
                    medium: vulCount.medium,
                    low: vulCount.low,
                };
            })
            .catch((e) => {
                return {};
            });

        findingsCriticalPromise = statisticService.get(
            "timeserie/finding.critical?days=" + severities_time + "&attr=last",
        );
        findingsHighPromise = statisticService.get(
            "timeserie/finding.high?days=" + severities_time + "&attr=last",
        );
        findingsMediumPromise = statisticService.get(
            "timeserie/finding.medium?days=" + severities_time + "&attr=last",
        );
        findingsLowPromise = statisticService.get(
            "timeserie/finding.low?days=" + severities_time + "&attr=last",
        );
        findingsOpenPromise = statisticService.get(
            "timeserie/finding.open?days=" + status_time + "&attr=last",
        );
        findingsAcceptedPromise = statisticService.get(
            "timeserie/finding.accepted?days=" + status_time + "&attr=last",
        );
        findingsFixedPromise = statisticService.get(
            "timeserie/finding.fixed?days=" + status_time + "&attr=last",
        );
        findingsFalsePositivePromise = statisticService.get(
            "timeserie/finding.false_positive?days=" +
                status_time +
                "&attr=last",
        );

        let dataPromises = await Promise.all([
            severityTotalsPromise,
            (findingCountPromise = statisticService
                .get("finding-count")
                .then((pFndCount) => (findingCount = pFndCount))
                .catch((e) => false)),
            (findingSeverityAgePromise = statisticService
                .get("finding-severity-age")
                .then((pVSA) => (findingSeverityAge = pVSA))
                .catch((e) => false)),
            (owasp10sPromise = statisticService
                .get("owasp-top10-2021")
                .catch((e) => false)),
            (cvesPromise = statisticService.get("top-cve").catch((e) => false)),
            (applicationsPromise = statisticService
                .get("top-application")
                .catch((e) => false)),
            (op_systemsPromise = statisticService
                .get("top-os")
                .catch((e) => false)),

            (dailyMaxCountPromises = Promise.all([
                findingsCriticalPromise,
                findingsHighPromise,
                findingsMediumPromise,
                findingsLowPromise,
            ])
                .then(([fCp, fHp, fMp, fLp]) => {
                    dailyMaxCountData = {
                        findingsCriticalData: fCp,
                        findingsHighData: fHp,
                        findingsMediumData: fMp,
                        findingsLowData: fLp,
                    };
                    return dailyMaxCountData;
                })
                .catch((e) => false)),
            (dailyStatusCountPromises = Promise.all([
                findingsOpenPromise,
                findingsAcceptedPromise,
                findingsFixedPromise,
                findingsFalsePositivePromise,
            ])
                .then(([fOp, fAp, fFp, fFPp]) => {
                    dailyStatusCountData = {
                        findingsOpenData: fOp,
                        findingsAcceptedData: fAp,
                        findingsFixedData: fFp,
                        findingsFalsePositiveData: fFPp,
                    };
                    return dailyStatusCountData;
                })
                .catch((e) => false)),
        ]);

        // update last refresh date
        dataUpdate.updated_at = new Date();
        if (refreshStatistics) refreshStatistics = false;
        return dataPromises;
    };

    let updateAssetCountData = async (kind) => {
        if (!token.access_token) return false;
        return (assetTotalsPromise = await searchAssetsCountByKind({
            filter: { kind: kind },
        }));
    };

    handleUpdateData(null, null, false);

    let autoUpdateInterval;

    // whenever the auto update data interval gets changed
    let handleAutoUpdateInvervalChange = () => {
        if (autoUpdateInterval) {
            // clear current interval and set the new one
            clearInterval(autoUpdateInterval);
        }
        if (dataUpdate.interval) {
            autoUpdateInterval = setInterval(
                handleUpdateData,
                dataUpdate.interval * 60 * 1000,
            );
        }
    };

    // call the interval to auto update data every 10 minutes (default value)
    handleAutoUpdateInvervalChange();

    const handleDataAutoUpdate = () => {
        is_tab_active = !document.hidden;
        if (!is_tab_active) {
            prevInterval = dataUpdate.interval;
            dataUpdate.interval = null;
        } else {
            dataUpdate.interval = prevInterval;
            let now = dayjs();
            //call server if more than 5 minutes passed
            if (now.subtract(5, "minutes") > dataUpdate.updated_at) {
                handleUpdateData(null, null, false);
            }
        }
        handleAutoUpdateInvervalChange();
    };

    onMount(async () => {
        handleWelcomeMessage();
        document.addEventListener("visibilitychange", handleDataAutoUpdate);
    });

    // when the component gets unmounted
    onDestroy(() => {
        // clear the auto update data interval
        clearInterval(autoUpdateInterval);
        document.removeEventListener("visibilitychange", handleDataAutoUpdate);
    });

    const reloadSeverityData = async (event, severities_time) => {
        event.stopPropagation();
        if (severities_time) severities_time++;
        if (!severities_time) severities_time = "31";

        findingsCriticalPromise = statisticService
            .get(
                "timeserie/finding.critical?days=" +
                    severities_time +
                    "&attr=last",
            )
            .catch((e) => false);
        findingsHighPromise = statisticService
            .get(
                "timeserie/finding.high?days=" + severities_time + "&attr=last",
            )
            .catch((e) => false);
        findingsMediumPromise = statisticService
            .get(
                "timeserie/finding.medium?days=" +
                    severities_time +
                    "&attr=last",
            )
            .catch((e) => false);
        findingsLowPromise = statisticService
            .get("timeserie/finding.low?days=" + severities_time + "&attr=last")
            .catch((e) => false);

        return await Promise.all([
            (dailyMaxCountPromises = Promise.all([
                findingsCriticalPromise,
                findingsHighPromise,
                findingsMediumPromise,
                findingsLowPromise,
            ])
                .then(([fCp, fHp, fMp, fLp]) => {
                    dailyMaxCountData = {
                        findingsCriticalData: fCp,
                        findingsHighData: fHp,
                        findingsMediumData: fMp,
                        findingsLowData: fLp,
                    };
                    return dailyMaxCountData;
                })
                .catch((e) => false)),
        ]);
    };

    const reloadStatusData = async (event, status_time) => {
        event.stopPropagation();
        if (status_time) status_time++;
        if (!status_time) status_time = "31";

        findingsOpenPromise = statisticService
            .get("timeserie/finding.open?days=" + status_time + "&attr=last")
            .catch((e) => false);
        findingsAcceptedPromise = statisticService
            .get(
                "timeserie/finding.accepted?days=" + status_time + "&attr=last",
            )
            .catch((e) => false);
        findingsFixedPromise = statisticService
            .get("timeserie/finding.fixed?days=" + status_time + "&attr=last")
            .catch((e) => false);
        findingsFalsePositivePromise = statisticService
            .get(
                "timeserie/finding.false_positive?days=" +
                    status_time +
                    "&attr=last",
            )
            .catch((e) => false);

        return await Promise.all([
            (dailyStatusCountPromises = Promise.all([
                findingsOpenPromise,
                findingsAcceptedPromise,
                findingsFixedPromise,
                findingsFalsePositivePromise,
            ])
                .then(([fOp, fAp, fFp, fFPp]) => {
                    dailyStatusCountData = {
                        findingsOpenData: fOp,
                        findingsAcceptedData: fAp,
                        findingsFixedData: fFp,
                        findingsFalsePositiveData: fFPp,
                    };
                    return dailyStatusCountData;
                })
                .catch((e) => false)),
        ]);
    };

    const handleWelcomeMessage = async () => {
        if (user.data.show_welcome_message && !user.data.showed_welcome_once) {
            showed_welcome_once = true;
            let result = await notificate("modal_welcome", "modal");
            if (result.value) {
                userService
                    .saveLoggedUserSettings(
                        { key: "show_welcome_msg", value: false },
                        [],
                        { no_toaster: true },
                    )
                    .then(() => (show_welcome_message = false))
                    .catch((e) => false);
            }
        }
    };
    $effect(() => {
        user.show_welcome_message = show_welcome_message;
        user.showed_welcome_once = showed_welcome_once;
    });

    /* START COPY DASHBOARD DATA FUNCTION */

    const copyAllDashboardData = async () => {
        try {
            // Determine current language with fallback
            const currentLang = $locale || "es";

            // Validate that we have access to clipboard API
            if (!navigator.clipboard) {
                throw new Error("Clipboard API not available in this browser");
            }

            // Safe data collection with validation
            const dashboardData = {
                // Timestamp of data collection
                [currentLang === "es" ? "fecha_de_exportación" : "export_date"]:
                    new Date().toISOString(),

                // Vulnerability severity statistics (with validation)
                [currentLang === "es"
                    ? "estadísticas_de_vulnerabilidades"
                    : "vulnerability_statistics"]:
                    findingCount && typeof findingCount === "object"
                        ? {
                              [currentLang === "es" ? "críticas" : "critical"]:
                                  findingCount.critical,
                              [currentLang === "es" ? "altas" : "high"]:
                                  findingCount.high,
                              [currentLang === "es" ? "medias" : "medium"]:
                                  findingCount.medium,
                              [currentLang === "es" ? "bajas" : "low"]:
                                  findingCount.low,
                          }
                        : {},

                // Asset counts by type (with better error handling)
                [currentLang === "es" ? "recuento_de_activos" : "asset_counts"]:
                    {
                        [currentLang === "es" ? "dominios" : "domains"]:
                            await safeGetAssetCount("domain"),
                        hostnames: await safeGetAssetCount("hostname"),
                        [currentLang === "es" ? "ips_publicas" : "public_ips"]:
                            await safeGetAssetCount("public_ip"),
                        [currentLang === "es" ? "ips_privadas" : "private_ips"]:
                            await safeGetAssetCount("private_ip"),
                    },

                // Findings by age data (if available)
                [currentLang === "es"
                    ? "hallazgos_por_antigüedad (días)"
                    : "findings_by_age (days)"]:
                    findingSeverityAge && typeof findingSeverityAge === "object"
                        ? findingSeverityAge
                        : {},

                // Daily maximum count data (if available)
                [currentLang === "es" ? "tendencias_diarias" : "daily_trends"]:
                    dailyMaxCountData && typeof dailyMaxCountData === "object"
                        ? dailyMaxCountData
                        : {},

                // Status trends data (if available)
                [currentLang === "es"
                    ? "tendencias_de_estado"
                    : "status_trends"]:
                    dailyStatusCountData &&
                    typeof dailyStatusCountData === "object"
                        ? dailyStatusCountData
                        : {},
            };

            // Format data as JSON
            const jsonData = JSON.stringify(dashboardData, null, 2);

            // Check clipboard permissions before attempting to write
            if (navigator.permissions) {
                try {
                    const writePermission = await navigator.permissions.query({
                        name: "clipboard-write",
                    });
                    if (writePermission.state === "denied") {
                        throw new Error("Clipboard write permission denied");
                    }
                } catch (permissionErr) {
                    console.warn(
                        "Permission check failed, proceeding anyway:",
                        permissionErr,
                    );
                }
            }

            // Copy to clipboard with error handling
            try {
                await navigator.clipboard.writeText(jsonData);
            } catch (clipboardErr) {
                console.warn(
                    "Clipboard API failed, trying fallback method:",
                    clipboardErr,
                );
                // Fallback to older execCommand method if Clipboard API fails
                const textArea = document.createElement("textarea");
                textArea.value = jsonData;
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                textArea.style.top = "-9999px";
                document.body.appendChild(textArea);
                textArea.select();

                try {
                    const successful = document.execCommand("copy");
                    document.body.removeChild(textArea);
                    if (!successful) throw new Error("Fallback copy failed");
                } catch (fallbackErr) {
                    document.body.removeChild(textArea);
                    throw new Error(
                        "Both Clipboard API and fallback failed: " +
                            fallbackErr.message,
                    );
                }
            }

            // Show success notification
            notificate("copied", "toast", {
                data: $format("copied"),
            });
        } catch (err) {
            console.error("Copy dashboard data error:", err);
            // Show error notification
            notificate("save_error", "toast", {
                data: $format("buttons.copy_to_clipboard_failed"),
            });
        }
    };

    // Helper function for safer asset count retrieval
    const safeGetAssetCount = async (assetType) => {
        try {
            if (!assetTotalsPromise) {
                return 0;
            }
            const result = await updateAssetCountData(assetType);
            const count = result?.[assetType] || 0;
            return count;
        } catch (err) {
            console.warn(`Failed to get ${assetType} count:`, err);
            return 0;
        }
    };

    /* END COPY DASHBOARD DATA FUNCTION */
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">
                    <i class="fas fa-chart-area fa-xs"></i>
                    {$format("page.dashboard.title")}
                </h1>
            </div>

            <div class="col-6">
                <p class="text-end" style="margin-top: 0.75em;">
                    {$format("page.dashboard.update_msg")}
                    <select
                        bind:value={dataUpdate.interval}
                        onblur={handleAutoUpdateInvervalChange}
                    >
                        {#each [null, 5, 10, 15, 30, 60] as interval}
                            <option value={interval}>
                                {interval
                                    ? interval
                                    : $format("page.dashboard.select_value")}
                            </option>
                        {/each}
                    </select>
                    {$format("page.dashboard.mins")}.

                    {$format("page.dashboard.last_update")}: {dataUpdate.updated_at.toLocaleTimeString(
                        "es-AR",
                    )}
                    <button
                        title={$format("buttons.refresh")}
                        class="btn btn-default btn-xs"
                        style="box-shadow: none !important; width: 24px;"
                        onclick={() => handleUpdateData(null, null, true)}
                    >
                        <i class="fas fa-sync-alt"></i>
                    </button>
                    <button
                        title={$format("buttons.copy_to_clipboard")}
                        class="btn btn-default btn-xs"
                        style="box-shadow: none !important; width: 24px;"
                        onclick={copyAllDashboardData}
                    >
                        <i class="fa fa-copy"></i>
                    </button>
                </p>
            </div>
        </div>
    </div>
</section>

{#await severityTotalsPromise then severityTotals}
    <section class="content">
        <div class="container-fluid">
            <SeverityTotals
                totals={severityTotals}
                redirectTo={"vulnerability"}
                title={$format(
                    "page.dashboard.components.vulnerabilitiesBySeverity.title",
                )}
            />
        </div>
    </section>
{/await}
{#await severityTotalsPromise}
    <section class="content severityBoxes-spinner">
        <div class="container-fluid">
            <div class="overlay text-center">
                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
            </div>
        </div>
    </section>
{/await}

<section class="content">
    <div class="container-fluid">
        <div class="row justify-content-between">
            <div class="col-12 d-flex mt-3" style="gap: 15px;">
                <div class="" style="flex: 1;">
                    <a href="/asset/domain" class="small-box-container mt-3">
                        <div class="small-box bg-white">
                            <div class="inner">
                                {#if !refreshStatistics}
                                    {#await updateAssetCountData("domain")}
                                        <div class="overlay">
                                            <i
                                                class="fas fa-2x fa-sync-alt fa-spin"
                                            ></i>
                                        </div>
                                    {:then assetTotals}
                                        <h3>{assetTotals["domain"] || 0}</h3>
                                        <p>
                                            {$format(
                                                "asset.kind.domain.plural",
                                            )}
                                        </p>
                                        <div class="icon">
                                            <i class="fas fa-globe"></i>
                                        </div>
                                    {/await}
                                {/if}
                            </div>
                        </div>
                    </a>
                </div>

                <div class="" style="flex: 1;">
                    <a href="/asset/hostname" class="small-box-container mt-3">
                        <div class="small-box bg-white">
                            <div class="inner">
                                {#if !refreshStatistics}
                                    {#await updateAssetCountData("hostname")}
                                        <div class="overlay">
                                            <i
                                                class="fas fa-2x fa-sync-alt fa-spin"
                                            ></i>
                                        </div>
                                    {:then assetTotals}
                                        <h3>{assetTotals["hostname"] || 0}</h3>
                                        <p>
                                            {$format(
                                                "asset.kind.hostname.plural",
                                            )}
                                        </p>
                                        <div class="icon">
                                            <i class="fas fa-laptop-code"></i>
                                        </div>
                                    {/await}
                                {/if}
                            </div>
                        </div>
                    </a>
                </div>

                <div class="" style="flex: 1;">
                    <a href="/asset/public_ip" class="small-box-container mt-3">
                        <div class="small-box bg-white">
                            <div class="inner">
                                {#if !refreshStatistics}
                                    {#await updateAssetCountData("public_ip")}
                                        <div
                                            class="overlay"
                                            style="display: flex; flex-direction: column; justify-content: center;"
                                        >
                                            <i
                                                class="fas fa-2x fa-sync-alt fa-spin"
                                            ></i>
                                        </div>
                                    {:then assetTotals}
                                        <h3>{assetTotals["public_ip"] || 0}</h3>
                                        <p>
                                            {$format(
                                                "asset.kind.public_ip.plural",
                                            )}
                                        </p>
                                        <div class="icon">
                                            <i class="fas fa-laptop"></i>
                                        </div>
                                    {/await}
                                {/if}
                            </div>
                        </div>
                    </a>
                </div>
                {#if !refreshStatistics}
                    {#await updateAssetCountData("private_ip")}
                        <div class="" style="flex: 1;">
                            <a
                                href="/asset/private_ip"
                                class="small-box-container mt-3"
                            >
                                <div class="small-box bg-white">
                                    <div class="inner">
                                        <div
                                            class="overlay"
                                            style="display: flex; flex-direction: column; justify-content: center;"
                                        >
                                            <i
                                                class="fas fa-2x fa-sync-alt fa-spin"
                                            ></i>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    {:then assetTotals}
                        {#if assetTotals["private_ip"] > 0}
                            <div class="" style="flex: 1;">
                                <a
                                    href="/asset/private_ip"
                                    class="small-box-container mt-3"
                                >
                                    <div class="small-box bg-white">
                                        <div class="inner">
                                            <h3>
                                                {assetTotals["private_ip"] || 0}
                                            </h3>
                                            <p>
                                                {$format(
                                                    "asset.kind.private_ip.plural",
                                                )}
                                            </p>
                                            <div class="icon">
                                                <i class="fas fa-laptop-house"
                                                ></i>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        {/if}
                    {/await}
                {/if}
            </div>
        </div>
        <div class="row">
            <div class="col-sm-12 col-xl-6">
                <div class="card" style="height: calc(100% - 1rem);">
                    <div class="card-header">
                        {$format(
                            "page.dashboard.components.findingsByAge.title",
                        )}
                        <i
                            class="ms-2 mt-2 icon-help help-btn far fa-question-circle"
                            class:showHelp={showHelp.vuls_by_age.persistent ||
                                showHelp.vuls_by_age.mouseover}
                            onclick={() =>
                                (showHelp.vuls_by_age.persistent =
                                    !showHelp.vuls_by_age.persistent)}
                            onmouseover={() =>
                                (showHelp.vuls_by_age.mouseover =
                                    !showHelp.vuls_by_age.mouseover)}
                            onfocus={() =>
                                (showHelp.vuls_by_age.mouseover =
                                    !showHelp.vuls_by_age.mouseover)}
                            onmouseleave={() =>
                                (showHelp.vuls_by_age.mouseover =
                                    !showHelp.vuls_by_age.mouseover)}
                        ></i>
                        <div>
                            {$format("page.dashboard.helpers.findingsByAge")}
                        </div>
                    </div>
                    <div class="card-body">
                        <div id="container">
                            {#await findingSeverityAgePromise then findingSeverityAge}
                                {#if !findingSeverityAge}
                                    <div
                                        class="text-center chart-has-no-data d-flex flex-column justify-content-center"
                                        style="height: 300px;"
                                    >
                                        <span
                                            ><strong
                                                >{$format(
                                                    "chart_no_data",
                                                )}</strong
                                            >.</span
                                        >
                                    </div>
                                {:else}
                                    <FindingsByAge {findingSeverityAge} />
                                {/if}
                            {/await}
                            {#await findingSeverityAgePromise}
                                <div class="overlay text-center">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"
                                    ></i>
                                </div>
                            {/await}
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-sm-12 col-xl-6">
                <div class="card" style="height: calc(100% - 1rem);">
                    <div class="card-header">
                        {$format(
                            "page.dashboard.components.findingsBySeverity.title",
                        )}
                        <i
                            class="ms-2 mt-2 icon-help help-btn far fa-question-circle"
                            role=""
                            class:showHelp={showHelp.vuls_by_severity
                                .persistent ||
                                showHelp.vuls_by_severity.mouseover}
                            onclick={() =>
                                (showHelp.vuls_by_severity.persistent =
                                    !showHelp.vuls_by_severity.persistent)}
                            onmouseover={() =>
                                (showHelp.vuls_by_severity.mouseover =
                                    !showHelp.vuls_by_severity.mouseover)}
                            onfocus={() =>
                                (showHelp.vuls_by_severity.mouseover =
                                    !showHelp.vuls_by_severity.mouseover)}
                            onmouseleave={() =>
                                (showHelp.vuls_by_severity.mouseover =
                                    !showHelp.vuls_by_severity.mouseover)}
                        ></i>
                        <div style="top: -44px;">
                            {$format(
                                "page.dashboard.helpers.findingsBySeverity",
                            )}
                        </div>
                    </div>
                    <div class="card-body">
                        {#await findingCountPromise then count}
                            <div>
                                <FindingsBySeverity findingCount={count} />
                            </div>
                        {/await}
                        {#await severityTotalsPromise}
                            <div class="overlay text-center">
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        {/await}
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-12 col-xl-6">
                <div class="card">
                    <div class="card-header">
                        {$format(
                            "page.dashboard.components.dailyMaxCount.title",
                        )}
                        <i
                            class="ms-2 mt-2 icon-help help-btn far fa-question-circle"
                            role=""
                            class:showHelp={showHelp.daily_max_count
                                .persistent ||
                                showHelp.daily_max_count.mouseover}
                            onclick={() =>
                                (showHelp.daily_max_count.persistent =
                                    !showHelp.daily_max_count.persistent)}
                            onmouseover={() =>
                                (showHelp.daily_max_count.mouseover =
                                    !showHelp.daily_max_count.mouseover)}
                            onfocus={() =>
                                (showHelp.daily_max_count.mouseover =
                                    !showHelp.daily_max_count.mouseover)}
                            onmouseleave={() =>
                                (showHelp.daily_max_count.mouseover =
                                    !showHelp.daily_max_count.mouseover)}
                        ></i>
                        <div style="top: -44px;">
                            {$format("page.dashboard.helpers.dailyMaxCount")}
                        </div>
                    </div>
                    <div class="card-body">
                        <div>
                            {#await dailyMaxCountPromises then data}
                                <DailyMaxCount
                                    findingsCriticalData={data.findingsCriticalData}
                                    findingsHighData={data.findingsHighData}
                                    findingsMediumData={data.findingsMediumData}
                                    findingsLowData={data.findingsLowData}
                                />
                                <div class="mt-3">
                                    <label
                                        >{$format("page.dashboard.see_last")}
                                        <input
                                            class="line-chart-input"
                                            type="number"
                                            placeholder="30"
                                            bind:value={severities_days}
                                            max="90"
                                            min="2"
                                        />
                                        <button
                                            class="btn btn-xs btn-primary btn-search"
                                            onclick={(event) =>
                                                reloadSeverityData(
                                                    event,
                                                    severities_days,
                                                )}
                                        >
                                            <i class="fas fa-search fa-fw"></i>
                                        </button>
                                        <span style="margin-left: -27px;"
                                            >{$format("page.dashboard.days")}
                                            <small
                                                >({$format(
                                                    "page.dashboard.min",
                                                )} 2 {$format(
                                                    "page.dashboard.days",
                                                )} - {$format(
                                                    "page.dashboard.max",
                                                )}: 90 {$format(
                                                    "page.dashboard.days",
                                                )})</small
                                            ></span
                                        >
                                    </label>
                                </div>
                            {/await}
                            {#await dailyMaxCountPromises}
                                <div class="overlay">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"
                                    ></i>
                                </div>
                            {/await}
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-sm-12 col-xl-6">
                <div class="card">
                    <div class="card-header">
                        {$format(
                            "page.dashboard.components.statusByDate.title",
                        )}
                        <i
                            class="ms-2 mt-2 icon-help help-btn far fa-question-circle"
                            role=""
                            class:showHelp={showHelp.status_max_count
                                .persistent ||
                                showHelp.status_max_count.mouseover}
                            onclick={() =>
                                (showHelp.status_max_count.persistent =
                                    !showHelp.status_max_count.persistent)}
                            onmouseover={() =>
                                (showHelp.status_max_count.mouseover =
                                    !showHelp.status_max_count.mouseover)}
                            onfocus={() =>
                                (showHelp.status_max_count.mouseover =
                                    !showHelp.status_max_count.mouseover)}
                            onmouseleave={() =>
                                (showHelp.status_max_count.mouseover =
                                    !showHelp.status_max_count.mouseover)}
                        ></i>
                        <div style="top: -64px;">
                            {$format("page.dashboard.helpers.statusByDate")}
                        </div>
                    </div>
                    <div class="card-body">
                        <div>
                            {#await dailyStatusCountPromises then data}
                                <StatusMaxCount
                                    findingsOpenData={data.findingsOpenData}
                                    findingsAcceptedData={data.findingsAcceptedData}
                                    findingsFixedData={data.findingsFixedData}
                                    findingsFalsePositiveData={data.findingsFalsePositiveData}
                                />
                                <div class="mt-3">
                                    <label
                                        >{$format("page.dashboard.see_last")}
                                        <input
                                            class="line-chart-input"
                                            type="number"
                                            placeholder="30"
                                            bind:value={status_days}
                                            max="91"
                                            min="2"
                                        />
                                        <button
                                            class="btn btn-xs btn-primary btn-search"
                                            onclick={(event) =>
                                                reloadStatusData(
                                                    event,
                                                    status_days,
                                                )}
                                        >
                                            <i class="fas fa-search fa-fw"></i>
                                        </button>
                                        <span style="margin-left: -27px;"
                                            >{$format("page.dashboard.days")}
                                            <small
                                                >({$format(
                                                    "page.dashboard.min",
                                                )} 2 {$format(
                                                    "page.dashboard.days",
                                                )} - {$format(
                                                    "page.dashboard.max",
                                                )}: 90 {$format(
                                                    "page.dashboard.days",
                                                )})</small
                                            ></span
                                        >
                                    </label>
                                </div>
                            {/await}
                            {#await dailyStatusCountPromises}
                                <div class="overlay">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"
                                    ></i>
                                </div>
                            {/await}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row mt-4">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        {$format(
                            "page.dashboard.components.patchingRoadmap.title",
                        )}
                        <i
                            class="ms-2 mt-2 icon-help help-btn far fa-question-circle"
                            role=""
                            class:showHelp={showHelp.patching_roadmap
                                .persistent ||
                                showHelp.patching_roadmap.mouseover}
                            onclick={() =>
                                (showHelp.patching_roadmap.persistent =
                                    !showHelp.patching_roadmap.persistent)}
                            onmouseover={() =>
                                (showHelp.patching_roadmap.mouseover =
                                    !showHelp.patching_roadmap.mouseover)}
                            onfocus={() =>
                                (showHelp.patching_roadmap.mouseover =
                                    !showHelp.patching_roadmap.mouseover)}
                            onmouseleave={() =>
                                (showHelp.patching_roadmap.mouseover =
                                    !showHelp.patching_roadmap.mouseover)}
                        ></i>
                        <div
                            style="top: -64px; font-size: 1rem; font-weight: normal; font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';"
                        >
                            {$format(
                                "page.dashboard.components.patchingRoadmap.description",
                            )}
                        </div>
                    </div>

                    <div class="card-body position-relative">
                        <!-- Filtro posicionado sobre la izquierda de la barra de búsqueda de PowerTable -->
                        <div
                            class="d-flex align-items-center"
                            style="position: absolute; top: 24px; left: 315px; z-index: 10;"
                        >
                            <label
                                for="minPriorityFilter"
                                class="m-0 me-2 text-muted"
                                style="font-size: 0.9em; font-weight: normal;"
                                >{$format(
                                    "page.dashboard.components.patchingRoadmap.min_priority",
                                )}</label
                            >
                            <input
                                id="minPriorityFilter"
                                type="number"
                                class="form-control form-control-sm line-chart-input"
                                style="width: 80px !important;"
                                step="0.1"
                                min="0"
                                max="10"
                                bind:value={minPriority}
                            />
                            <button
                                class="btn btn-xs btn-primary btn-search btn-patching-roadmap"
                                onclick={loadPatchingRoadmap}
                            >
                                <i class="fas fa-search fa-fw"></i>
                            </button>
                        </div>

                        <div class="overlay-wrapper">
                            <div
                                class="text-center"
                                class:overlay={is_requesting_roadmap}
                            >
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        </div>

                        {#if roadmapData.length > 0}
                            <div class="table-responsive">
                                <PowerTable
                                    ptData={roadmapData}
                                    ptInstructs={instructsRoadmap}
                                    ptOptions={ptOptionsRoadmap}
                                    bind:this={roadmapTableElement}
                                />
                            </div>
                        {:else if !is_requesting_roadmap}
                            <div class="text-center p-4 text-muted mt-5">
                                {$format(
                                    "page.dashboard.components.patchingRoadmap.no_products",
                                )}
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>

<style>
    .severityBoxes-spinner {
        height: 200px;
        padding-top: 50px;
    }
    a.severityTotalsBoxes {
        color: rgb(33, 37, 41) !important;
    }
    a.severityTotalsBoxes:hover,
    a.severityTotalsBoxes:active,
    a.severityTotalsBoxes:focus {
        color: inherit !important;
    }

    .help-btn {
        position: relative;
    }

    .help-btn + div {
        display: none;
        position: absolute;
        top: -87px;
        right: 0;
        background: rgb(0, 0, 0, 0.8);
        border-radius: 5px;
        color: white;
        padding: 15px;
        z-index: 1000;
    }

    .help-btn + div:after {
        content: "";
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid rgba(0, 0, 0, 0.8);
        position: absolute;
        bottom: -10px;
        right: 16px;
    }

    .help-btn.showHelp + div {
        display: block;
    }

    i.fas.fa-sort,
    i.fas.fa-sort-down,
    i.fas.fa-sort-up {
        color: #888 !important;
        font-size: 12px;
        position: relative;
        top: -5px;
        right: 10px;
    }

    @media (max-width: 1200px) {
        .small-box .icon {
            display: none;
        }
    }

    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    input[type="number"] {
        -moz-appearance: textfield;
        margin: 0;
    }

    .line-chart-input {
        width: 70px !important;
        border: none;
        padding-left: 5px;
        border: 1px solid #ced4da;
        border-radius: 3px;
    }

    .line-chart-input:focus {
        box-shadow: none !important;
    }

    .line-chart-input:focus-visible {
        border: 1px solid #80bdff !important;
        outline: none !important;
    }

    .btn-search {
        position: relative;
        left: -29px;
        height: 27px;
        top: -1px;
        border-radius: 3px;
    }
    .btn-search i {
        position: relative;
        top: -4px;
    }

    .btn-search.btn-patching-roadmap {
        position: relative;
        left: -29px;
        height: 31px;
        border-radius: 3px;
        top: 0;
    }
    .inner {
        min-height: 7em;
    }
    .overlay-wrapper div {
        display: none;
    }
    .overlay-wrapper div.overlay {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-top: 20px;
        margin-bottom: 20px;
    }
    .overlay {
        min-height: 5em;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    :global([data-prefix="roadmapTable"] tfoot) {
        display: none !important;
    }
</style>
